import React, { useEffect, useRef, useState } from 'react';
import mx from 'mxgraph';
import {
  FaPlay, FaStop, FaCode, FaDatabase, FaQuestion,
  FaArrowRight, FaRedo, FaUndo, FaSave, FaTrash,
  FaCog, FaPlus, FaMinus, FaExpand, FaCopy, FaPalette
} from 'react-icons/fa';

const { 
  mxGraph, 
  mxRubberband, 
  mxClient, 
  mxEvent, 
  mxUtils, 
  mxConstants, 
  mxPerimeter,
  mxCell,
  mxGeometry,
  mxPoint,
  mxConnectionConstraint,
  mxEllipse,
  mxGraphView,
  mxGuide,
  mxKeyHandler,
  mxUndoManager
} = mx();

const FlowchartEditor = () => {
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const [graph, setGraph] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    cell: null,
    isEdge: false
  });

  useEffect(() => {
    if (!containerRef.current || graphRef.current) return;

    if (!mxClient.isBrowserSupported()) {
      mxUtils.error('Browser not supported!', 200, false);
      return;
    }

    const newGraph = new mxGraph(containerRef.current);
    graphRef.current = newGraph;
    setGraph(newGraph);

    // Grid ayarları
    newGraph.gridSize = 20;
    newGraph.gridEnabled = true;
    newGraph.graphHandler.guidesEnabled = true;

    // Grid görünümü için container style
    if (containerRef.current) {
      containerRef.current.style.backgroundImage = `
        linear-gradient(to right, rgba(60, 60, 60, 0.1) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(60, 60, 60, 0.1) 1px, transparent 1px)
      `;
      containerRef.current.style.backgroundSize = '20px 20px';
      containerRef.current.style.backgroundColor = '#1e1e1e';
    }

    // Zoom ayarları
    newGraph.centerZoom = true;
    newGraph.mouseWheelFunction = (evt) => {
      if (mxEvent.isControlDown(evt)) {
        if (evt.deltaY < 0) {
          newGraph.zoomIn();
          setZoom(newGraph.view.scale);
        } else {
          newGraph.zoomOut();
          setZoom(newGraph.view.scale);
        }
        mxEvent.consume(evt);
      }
    };

    // Edge stil ayarları
    const edgeStyle = newGraph.getStylesheet().getDefaultEdgeStyle();
    Object.assign(edgeStyle, {
      [mxConstants.STYLE_ROUNDED]: true,
      [mxConstants.STYLE_STROKEWIDTH]: 2,
      [mxConstants.STYLE_STROKECOLOR]: '#666666',
      [mxConstants.STYLE_EDGE]: mxConstants.EDGESTYLE_ORTHOGONAL,
      [mxConstants.STYLE_ENDARROW]: mxConstants.ARROW_CLASSIC,
      [mxConstants.STYLE_CURVED]: 1,
      [mxConstants.STYLE_OPACITY]: 75,
      [mxConstants.STYLE_SHADOW]: true
    });

    // Tarayıcının varsayılan context menüsünü engelle
    containerRef.current.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // Mouse event listener'ı güncellendi
    const mouseListener = {
      mouseDown: function(sender, evt) {
        if (mxEvent.isPopupTrigger(evt.getEvent())) {
          const cell = evt.getCell();
          evt.consume(); // Her durumda event'i tüket
          
          if (cell) {
            const point = mxUtils.convertPoint(
              containerRef.current,
              evt.getEvent().clientX,
              evt.getEvent().clientY
            );

            setContextMenu({
              visible: true,
              x: point.x,
              y: point.y,
              cell,
              isEdge: cell.isEdge()
            });
          } else {
            // Boş alana sağ tıklandığında menüyü kapat
            setContextMenu(prev => ({ ...prev, visible: false }));
          }
        }
      },
      mouseMove: function() {},
      mouseUp: function() {}
    };

    newGraph.addMouseListener(mouseListener);

    // Boş alana tıklandığında context menüyü kapat
    const handleDocumentClick = (e) => {
      if (!e.target.closest('.context-menu')) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };

    document.addEventListener('click', handleDocumentClick);

    // Hizalama kılavuzları
    new mxGuide(newGraph, newGraph.graphHandler.getGuideStates());

    // Klavye kısayolları
    new mxKeyHandler(newGraph);

    // Undo/Redo yönetimi
    const undoManager = new mxUndoManager();
    const listener = function(sender, evt) {
      undoManager.undoableEditHappened(evt.getProperty('edit'));
    };
    newGraph.getModel().addListener(mxEvent.UNDO, listener);
    newGraph.getView().addListener(mxEvent.UNDO, listener);

    // Temel ayarlar
    newGraph.setEnabled(true);
    newGraph.setConnectable(true);
    newGraph.setCellsEditable(true);
    newGraph.setCellsResizable(true);
    newGraph.setDropEnabled(true);
    newGraph.setPanning(true);
    newGraph.setTooltips(true);
    newGraph.setMultigraph(false);

    // Edge ayarları
    newGraph.connectionHandler.createTarget = true;
    
    // Default edge style
    const style = newGraph.getStylesheet().getDefaultEdgeStyle();
    style[mxConstants.STYLE_ROUNDED] = true;
    style[mxConstants.STYLE_STROKEWIDTH] = 2;
    style[mxConstants.STYLE_STROKECOLOR] = '#666666';
    style[mxConstants.STYLE_EDGE] = mxConstants.EDGESTYLE_ORTHOGONAL;
    style[mxConstants.STYLE_ENDARROW] = mxConstants.ARROW_CLASSIC;

    // Edge oluşturma
    newGraph.connectionHandler.addListener(mxEvent.CONNECT, (sender, evt) => {
      const edge = evt.getProperty('cell');
      const source = edge.source;
      const target = edge.target;
      
      if (source && target) {
        const geometry = edge.geometry || new mxGeometry();
        geometry.relative = true;
        edge.geometry = geometry;
      }
    });

    // Bağlantı noktaları
    newGraph.getAllConnectionConstraints = (terminal) => {
      if (terminal && terminal.shape) {
        return [
          new mxConnectionConstraint(new mxPoint(0.5, 0), true),
          new mxConnectionConstraint(new mxPoint(0.5, 1), true),
          new mxConnectionConstraint(new mxPoint(0, 0.5), true),
          new mxConnectionConstraint(new mxPoint(1, 0.5), true)
        ];
      }
      return null;
    };

    // Vertex stilleri
    const vertexStyle = newGraph.getStylesheet().getDefaultVertexStyle();
    vertexStyle[mxConstants.STYLE_SHAPE] = mxConstants.SHAPE_RECTANGLE;
    vertexStyle[mxConstants.STYLE_PERIMETER] = mxPerimeter.RectanglePerimeter;
    vertexStyle[mxConstants.STYLE_STROKEWIDTH] = 2;
    vertexStyle[mxConstants.STYLE_FONTCOLOR] = '#FFFFFF';
    vertexStyle[mxConstants.STYLE_FONTSIZE] = '12';
    vertexStyle[mxConstants.STYLE_FONTSTYLE] = 1;
    vertexStyle[mxConstants.STYLE_ROUNDED] = true;
    vertexStyle[mxConstants.STYLE_SHADOW] = true;

    // Özel şekil stilleri
    const styles = {
      start: {
        shape: mxConstants.SHAPE_ELLIPSE,
        fillColor: '#4CAF50',
        strokeColor: '#388E3C',
      },
      end: {
        shape: mxConstants.SHAPE_ELLIPSE,
        fillColor: '#F44336',
        strokeColor: '#D32F2F',
      },
      process: {
        shape: mxConstants.SHAPE_RECTANGLE,
        fillColor: '#2196F3',
        strokeColor: '#1976D2',
      },
      decision: {
        shape: mxConstants.SHAPE_RHOMBUS,
        fillColor: '#FFC107',
        strokeColor: '#FFA000',
      },
      input: {
        shape: mxConstants.SHAPE_PARALLELOGRAM,
        fillColor: '#9C27B0',
        strokeColor: '#7B1FA2',
      },
      output: {
        shape: mxConstants.SHAPE_PARALLELOGRAM,
        fillColor: '#FF5722',
        strokeColor: '#E64A19',
      },
      database: {
        shape: mxConstants.SHAPE_CYLINDER,
        fillColor: '#607D8B',
        strokeColor: '#455A64',
      },
      subProcess: {
        shape: mxConstants.SHAPE_RECTANGLE,
        fillColor: '#3F51B5',
        strokeColor: '#303F9F',
      }
    };

    // Stilleri kaydet
    Object.entries(styles).forEach(([key, style]) => {
      const styleStr = Object.entries(style)
        .map(([k, v]) => `${k}=${v}`)
        .join(';');
      newGraph.getStylesheet().putCellStyle(key, {
        ...newGraph.getStylesheet().getDefaultVertexStyle(),
        [mxConstants.STYLE_SHAPE]: style.shape,
        [mxConstants.STYLE_FILLCOLOR]: style.fillColor,
        [mxConstants.STYLE_STROKECOLOR]: style.strokeColor,
      });
    });

    new mxRubberband(newGraph);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      if (containerRef.current) {
        containerRef.current.removeEventListener('contextmenu', (e) => {
          e.preventDefault();
        });
      }
      if (graphRef.current) {
        graphRef.current.destroy();
        graphRef.current = null;
        setGraph(null);
      }
    };
  }, []);

  const handleAddVertex = (type) => {
    if (!graph) return;

    const parent = graph.getDefaultParent();
    graph.getModel().beginUpdate();
    
    try {
      const dimensions = {
        start: { w: 80, h: 80 },
        end: { w: 80, h: 80 },
        process: { w: 120, h: 60 },
        decision: { w: 100, h: 100 },
        input: { w: 120, h: 60 },
        output: { w: 120, h: 60 },
        database: { w: 100, h: 80 },
        subProcess: { w: 140, h: 60 }
      };

      const { w, h } = dimensions[type] || dimensions.process;

      graph.insertVertex(
        parent,
        null,
        type.charAt(0).toUpperCase() + type.slice(1),
        20,
        20,
        w,
        h,
        type
      );
    } finally {
      graph.getModel().endUpdate();
    }
  };

  // Zoom işlemleri
  const handleZoomIn = () => {
    if (graph) {
      graph.zoomIn();
      setZoom(graph.view.scale);
    }
  };

  const handleZoomOut = () => {
    if (graph) {
      graph.zoomOut();
      setZoom(graph.view.scale);
    }
  };

  const handleResetZoom = () => {
    if (graph) {
      graph.zoomActual();
      setZoom(1);
    }
  };

  const handleFitZoom = () => {
    if (graph) {
      graph.fit();
      setZoom(graph.view.scale);
    }
  };

  // Hücre silme işlemi
  const handleDeleteCell = () => {
    if (graph && contextMenu.cell) {
      const cells = [contextMenu.cell];
      
      // Eğer bağlı edge'ler varsa onları da sil
      if (!contextMenu.isEdge) {
        const edges = graph.getEdges(contextMenu.cell);
        if (edges && edges.length > 0) {
          cells.push(...edges);
        }
      }
      
      graph.removeCells(cells, true);
      setContextMenu(prev => ({ ...prev, visible: false }));
    }
  };

  // Klavye kısayolu ile silme
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && graph) {
        const cells = graph.getSelectionCells();
        if (cells && cells.length > 0) {
          graph.removeCells(cells, true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [graph]);

  return (
    <div className="h-full flex flex-col bg-[#1e1e1e]">
      {/* Gelişmiş Toolbar */}
      <div className="p-2 bg-[#2d2d2d] border-b border-gray-700">
        <div className="flex items-center justify-between mb-2">
          {/* Sol toolbar grubu */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 bg-[#383838] rounded-md p-1">
              <button className="toolbar-btn" onClick={() => graph?.undo()}>
                <FaUndo />
              </button>
              <button className="toolbar-btn" onClick={() => graph?.redo()}>
                <FaRedo />
              </button>
            </div>
            
            <div className="h-4 w-px bg-gray-700" />
            
            <div className="flex items-center space-x-1 bg-[#383838] rounded-md p-1">
              <button className="toolbar-btn" >
                <FaSave />
              </button>
              <button className="toolbar-btn" onClick={() => graph?.removeCells(graph.getSelectionCells())}>
                <FaTrash />
              </button>
            </div>
          </div>

          {/* Sağ toolbar grubu */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 bg-[#383838] rounded-md p-1">
              <button className="toolbar-btn" onClick={handleZoomOut}>
                <FaMinus />
              </button>
              <span className="text-white text-sm px-2">
                {Math.round(zoom * 100)}%
              </span>
              <button className="toolbar-btn" onClick={handleZoomIn}>
                <FaPlus />
              </button>
            </div>
            <button className="toolbar-btn" onClick={handleFitZoom}>
              <FaExpand />
            </button>
          </div>
        </div>

        {/* Şekiller Toolbar */}
        <div className="flex flex-wrap gap-2">
          {/* Start Node */}
          <button
            onClick={() => handleAddVertex('start')}
            className="flowchart-shape-btn"
          >
            <div className="w-8 h-8 rounded-full bg-green-600 border-2 border-green-700 flex items-center justify-center">
              <FaPlay className="text-white text-sm" />
            </div>
            <span className="text-xs text-white mt-1">Start</span>
          </button>

          {/* Process Node */}
          <button
            onClick={() => handleAddVertex('process')}
            className="flowchart-shape-btn"
          >
            <div className="w-12 h-8 rounded bg-blue-600 border-2 border-blue-700 flex items-center justify-center">
              <FaCode className="text-white text-sm" />
            </div>
            <span className="text-xs text-white mt-1">Process</span>
          </button>

          {/* Decision Node */}
          <button
            onClick={() => handleAddVertex('decision')}
            className="flowchart-shape-btn"
          >
            <div className="w-8 h-8 transform rotate-45 bg-yellow-600 border-2 border-yellow-700 flex items-center justify-center">
              <FaQuestion className="text-white text-sm -rotate-45" />
            </div>
            <span className="text-xs text-white mt-1">Decision</span>
          </button>

          {/* Input Node */}
          <button
            onClick={() => handleAddVertex('input')}
            className="flowchart-shape-btn"
          >
            <div className="w-12 h-8 bg-purple-600 border-2 border-purple-700 flex items-center justify-center"
                 style={{ clipPath: 'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)' }}>
              <FaArrowRight className="text-white text-sm" />
            </div>
            <span className="text-xs text-white mt-1">Input</span>
          </button>

          {/* Output Node */}
          <button
            onClick={() => handleAddVertex('output')}
            className="flowchart-shape-btn"
          >
            <div className="w-12 h-8 bg-orange-600 border-2 border-orange-700 flex items-center justify-center"
                 style={{ clipPath: 'polygon(0% 0%, 80% 0%, 100% 100%, 20% 100%)' }}>
              <FaArrowRight className="text-white text-sm rotate-180" />
            </div>
            <span className="text-xs text-white mt-1">Output</span>
          </button>

          {/* Database Node */}
          <button
            onClick={() => handleAddVertex('database')}
            className="flowchart-shape-btn"
          >
            <div className="w-10 h-8 bg-gray-600 border-2 border-gray-700 rounded-t-lg flex items-center justify-center relative">
              <div className="absolute -bottom-1 w-full h-2 bg-gray-600 border-x-2 border-b-2 border-gray-700 rounded-b-lg"></div>
              <FaDatabase className="text-white text-sm" />
            </div>
            <span className="text-xs text-white mt-1">Database</span>
          </button>

          {/* Sub-Process Node */}
          <button
            onClick={() => handleAddVertex('subProcess')}
            className="flowchart-shape-btn"
          >
            <div className="w-12 h-8 bg-indigo-600 border-2 border-indigo-700 rounded flex items-center justify-center relative">
              <div className="absolute -left-1 -right-1 h-1 bg-indigo-600 border-x-2 border-indigo-700"></div>
              <FaCog className="text-white text-sm" />
            </div>
            <span className="text-xs text-white mt-1">Sub-Process</span>
          </button>

          {/* End Node */}
          <button
            onClick={() => handleAddVertex('end')}
            className="flowchart-shape-btn"
          >
            <div className="w-8 h-8 rounded-full bg-red-600 border-2 border-red-700 flex items-center justify-center">
              <FaStop className="text-white text-sm" />
            </div>
            <span className="text-xs text-white mt-1">End</span>
          </button>
        </div>
      </div>

      {/* Graph Container - Grid arka plan için CSS class ekledik */}
      <div 
        ref={containerRef} 
        className="flex-1 graph-container"
        style={{
          position: 'relative',
          overflow: 'auto'
        }}
      />

      {/* Gelişmiş Context Menu */}
      {contextMenu.visible && (
        <div 
          className="fixed z-50 bg-[#2d2d2d] rounded-md shadow-lg border border-gray-700 context-menu"
          style={{ 
            left: contextMenu.x, 
            top: contextMenu.y,
            minWidth: '180px'
          }}
          onClick={e => e.stopPropagation()}
          onContextMenu={e => e.preventDefault()}
        >
          <button
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
            onClick={handleDeleteCell}
          >
            <FaTrash className="text-sm" />
            <span>Delete</span>
            <span className="ml-auto text-xs text-gray-400">Del</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
            onClick={() => {
              if (graph && contextMenu.cell) {
                graph.copyCell(contextMenu.cell);
              }
            }}
          >
            <FaCopy className="text-sm" />
            <span>Copy</span>
            <span className="ml-auto text-xs text-gray-400">Ctrl+C</span>
          </button>
          {!contextMenu.isEdge && (
            <button
              className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
              onClick={() => {
                if (graph && contextMenu.cell) {
                  const style = graph.getCellStyle(contextMenu.cell);
                  // Style düzenleme modalı açılabilir
                }
              }}
            >
              <FaPalette className="text-sm" />
              <span>Style</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FlowchartEditor; 